import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  doc, getDoc, setDoc, collection, query, where, getDocs,
} from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../firebase';
import { hashPin } from '../lib/hash';

const AuthContext = createContext(null);

const STORAGE_KEY = 'dnd-vtt-auth';

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const persist = (data) => {
    setAuth(data);
    if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    else localStorage.removeItem(STORAGE_KEY);
  };

  // ── DM Login ──────────────────────────────────────────────────────────────
  const loginDm = useCallback(async (pin) => {
    setLoading(true);
    setError('');
    try {
      const campaignRef  = doc(db, 'campaigns', CAMPAIGN_ID);
      const campaignSnap = await getDoc(campaignRef);

      if (!campaignSnap.exists()) {
        // First run — create the campaign with this PIN as the master PIN.
        const pinHash = await hashPin(pin);
        await setDoc(campaignRef, {
          name:        'My Campaign',
          dmPinHash:   pinHash,
          created:     Date.now(),
          activeMapId: null,
          mode:        'exploration',
          version:     1,
        });
        persist({ role: 'dm', campaignId: CAMPAIGN_ID });
        return { ok: true, firstRun: true };
      }

      const { dmPinHash } = campaignSnap.data();
      const pinHash = await hashPin(pin);
      if (pinHash !== dmPinHash) {
        setError('Incorrect DM PIN.');
        return { ok: false };
      }
      persist({ role: 'dm', campaignId: CAMPAIGN_ID });
      return { ok: true };
    } catch (err) {
      console.error('DM login error', err);
      setError('Login failed. Check your connection.');
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Player Login ──────────────────────────────────────────────────────────
  const loginPlayer = useCallback(async (name, pin) => {
    setLoading(true);
    setError('');
    try {
      const trimmedName = name.trim();
      if (!trimmedName) { setError('Enter your name.'); return { ok: false }; }

      const playersRef = collection(db, 'campaigns', CAMPAIGN_ID, 'players');
      const q          = query(playersRef, where('name', '==', trimmedName));
      const snap       = await getDocs(q);
      const pinHash    = await hashPin(pin);

      if (snap.empty) {
        // New player — create their record.
        const newRef = doc(playersRef);
        await setDoc(newRef, {
          name:      trimmedName,
          pinHash,
          tokenId:   null,
          class:     null,
          level:     1,
          connected: true,
          lastSeen:  Date.now(),
        });
        persist({ role: 'player', playerId: newRef.id, name: trimmedName, campaignId: CAMPAIGN_ID });
        return { ok: true, firstRun: true };
      }

      const playerDoc  = snap.docs[0];
      const playerData = playerDoc.data();

      if (playerData.pinHash !== pinHash) {
        setError('Incorrect PIN.');
        return { ok: false };
      }

      persist({ role: 'player', playerId: playerDoc.id, name: trimmedName, campaignId: CAMPAIGN_ID });
      return { ok: true };
    } catch (err) {
      console.error('Player login error', err);
      setError('Login failed. Check your connection.');
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => persist(null), []);

  return (
    <AuthContext.Provider value={{ auth, loading, error, loginDm, loginPlayer, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
